import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertyNoteSummary } from '@/redux/internal-services/workspaceApiExtensions';
import { useCreatePropertyNoteMutation } from '@/redux/internal-services/workspaceApiExtensions';

// note_type → readable display category
const CATEGORY_LABEL: Record<string, string> = {
  measurement: 'Scope',
  inspection: 'Scope',
  site_observation: 'Scope',
  access: 'Site & access',
  safety: 'Safety',
  special_instruction: 'Special instructions',
  customer_preference: 'Customer request',
  general: 'General',
};

// Category chips shown in the filter bar (ordered as in mockup)
const FILTER_CHIPS = ['Site & access', 'Special instructions', 'Scope', 'Customer request', 'Safety', 'General'];

// note_type values that belong to a given display category
const TYPES_FOR_CATEGORY: Record<string, string[]> = {
  'Site & access': ['access'],
  'Special instructions': ['special_instruction'],
  'Scope': ['measurement', 'inspection', 'site_observation'],
  'Customer request': ['customer_preference'],
  'Safety': ['safety'],
  'General': ['general'],
};

const NOTE_TYPE_OPTIONS = [
  { value: 'access', label: 'Site & access' },
  { value: 'special_instruction', label: 'Special instructions' },
  { value: 'measurement', label: 'Scope' },
  { value: 'customer_preference', label: 'Customer request' },
  { value: 'safety', label: 'Safety' },
  { value: 'general', label: 'General' },
];

function fmtNoteDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function VoiceNoteCard({ note }: { note: PropertyNoteSummary }) {
  const [playing, setPlaying] = useState(false);
  const transcript = note.clean_transcript || note.final_transcript;
  const category = CATEGORY_LABEL[note.note_type] ?? note.note_type;

  function handlePlay() {
    if (!note.audio_url) return;
    const audio = new Audio(note.audio_url);
    setPlaying(true);
    audio.play().catch(() => {});
    audio.onended = () => setPlaying(false);
  }

  return (
    <div className="flex gap-3 items-start rounded-[10px] border border-ui-border p-3">
      {/* Play button */}
      <button
        type="button"
        onClick={handlePlay}
        disabled={!note.audio_url}
        className="shrink-0 flex h-[30px] w-[30px] items-center justify-center rounded-full bg-brand-light/20 text-brand-dark text-[12px] disabled:opacity-40"
      >
        {playing ? '■' : '▶'}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <b className="text-[13px] text-ui-text">{category}</b>
        </div>
        {note.created_on && (
          <p className="text-[12px] text-ui-text3 mt-0.5">{fmtNoteDate(note.created_on)}</p>
        )}
        {transcript ? (
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-ui-text text-pretty">
            "{transcript}"
          </p>
        ) : (
          <button
            type="button"
            className="mt-1.5 rounded-[7px] border border-ui-border bg-white px-2.5 py-1 text-[11.5px] font-semibold text-ui-text2 hover:bg-ui-subtle dark:bg-ui-card"
          >
            Transcribe
          </button>
        )}
      </div>

      {note.audio_url && (
        <a
          href={note.audio_url}
          download
          className="shrink-0 text-[13px] text-ui-text3 hover:text-ui-text"
          title="Download audio"
        >
          ⬇
        </a>
      )}
      <span className="shrink-0 text-[13px] text-ui-text3 tracking-[1px]">•••</span>
    </div>
  );
}

function TextNoteCard({ note }: { note: PropertyNoteSummary }) {
  const body = note.review_summary || note.clean_transcript || note.final_transcript || note.title || '';
  const category = CATEGORY_LABEL[note.note_type] ?? note.note_type;

  return (
    <div
      className="rounded-[10px] border border-ui-border px-3 py-[11px]"
      style={{ borderLeftWidth: 3, borderLeftColor: '#8C6BE8' }}
    >
      <div className="flex items-baseline gap-2">
        <b className="flex-1 min-w-0 text-[12.5px] text-brand-dark">{category}</b>
        <span className="shrink-0 text-[13px] text-ui-text3 tracking-[1px]">•••</span>
      </div>
      {body && (
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-ui-text text-pretty">{body}</p>
      )}
    </div>
  );
}

interface NotesModalProps {
  onClose: () => void;
  propertyId: string;
  engagementId: string;
  notes: PropertyNoteSummary[];
  address?: string;
}

export function NotesModal({ onClose, propertyId, engagementId, notes, address }: NotesModalProps) {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('access');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [createNote, { isLoading: saving }] = useCreatePropertyNoteMutation();

  const voiceNotes = notes.filter(n => n.source === 'voice' || !!n.audio_url);
  const textNotes = notes.filter(n => n.source !== 'voice' && !n.audio_url);

  function matchesFilter(note: PropertyNoteSummary) {
    if (activeFilter === 'All') return true;
    const allowed = TYPES_FOR_CATEGORY[activeFilter] ?? [];
    return allowed.includes(note.note_type);
  }

  const visibleVoice = voiceNotes.filter(matchesFilter);
  const visibleText = textNotes.filter(matchesFilter);

  // Only show filter chips that have at least one note
  const usedCategories = new Set(notes.map(n => CATEGORY_LABEL[n.note_type] ?? n.note_type));
  const chips = FILTER_CHIPS.filter(c => usedCategories.has(c));

  async function handleSave() {
    if (!noteText.trim()) return;
    setSaveError(null);
    try {
      await createNote({
        propertyId,
        body: {
          text_content: noteText.trim(),
          note_type: noteType,
          title: noteText.slice(0, 60),
          engagement_id: engagementId,
        },
      }).unwrap();
      setNoteText('');
      setAddNoteOpen(false);
    } catch {
      setSaveError(t('notes.saveFailed', 'Could not save — please try again.'));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative my-6 w-full overflow-auto rounded-2xl border border-ui-border bg-white shadow-[0_18px_48px_rgba(12,10,30,0.28)] dark:bg-ui-card"
        style={{ maxWidth: 680, maxHeight: '88vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-ui-border px-5 py-[18px] pb-3.5">
          <div className="flex-1 min-w-0">
            <p className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-ui-text3">
              {t('jobs.notesLabel', 'Notes')}
            </p>
            {address && (
              <p className="mt-0.5 text-[17px] font-bold text-ui-text">{address}</p>
            )}
            <p className="mt-0.5 text-[12px] text-ui-text3">
              {t('jobs.notesSubtitle', 'Voice and text notes captured against this job · visible to the PM and crew lead')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 flex h-[28px] w-[28px] items-center justify-center rounded-full border border-ui-border bg-white text-[14px] text-ui-text2 hover:bg-ui-subtle dark:bg-ui-card"
          >
            ×
          </button>
        </div>

        <div className="flex flex-col gap-[18px] px-5 py-4">
          {/* Filter chips */}
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {['All', ...chips].map(chip => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => setActiveFilter(chip)}
                  className={`rounded-full px-3 py-[5px] text-[11.5px] font-semibold border transition-colors ${
                    activeFilter === chip
                      ? 'border-brand-dark bg-brand-dark text-white'
                      : 'border-ui-border bg-white text-ui-text2 hover:bg-ui-subtle dark:bg-ui-card'
                  }`}
                >
                  {chip}
                </button>
              ))}
            </div>
          )}

          {/* VOICE NOTES */}
          {(visibleVoice.length > 0 || voiceNotes.length > 0) && (
            <div>
              <div className="flex items-center gap-2 mb-2.5">
                <p className="flex-1 text-[11px] font-bold tracking-[0.09em] uppercase text-ui-text3">
                  {t('jobs.voiceNotes', 'Voice notes')} ({visibleVoice.length})
                </p>
                <button type="button" className="text-[12.5px] font-semibold text-brand-dark">
                  + {t('jobs.record', 'Record')}
                </button>
              </div>
              {visibleVoice.length > 0 ? (
                <div className="flex flex-col gap-[9px]">
                  {visibleVoice.map(n => <VoiceNoteCard key={n.note_id} note={n} />)}
                </div>
              ) : (
                <p className="text-[12px] text-ui-text3 py-2">
                  {t('jobs.noVoiceNotes', 'No voice notes in this category.')}
                </p>
              )}
            </div>
          )}

          {/* TEXT NOTES */}
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <p className="flex-1 text-[11px] font-bold tracking-[0.09em] uppercase text-ui-text3">
                {t('jobs.notes', 'Notes')} ({visibleText.length})
              </p>
              <button
                type="button"
                onClick={() => setAddNoteOpen(v => !v)}
                className="text-[12.5px] font-semibold text-brand-dark"
              >
                + {t('jobs.addNote', 'Add note')}
              </button>
            </div>

            {/* Add note form */}
            {addNoteOpen && (
              <div className="mb-3 rounded-xl border border-ui-border bg-ui-subtle p-3 flex flex-col gap-2">
                <div className="flex flex-wrap gap-1.5">
                  {NOTE_TYPE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setNoteType(opt.value)}
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold border ${
                        noteType === opt.value
                          ? 'bg-brand-dark text-white border-brand-dark'
                          : 'bg-white text-ui-text2 border-ui-border dark:bg-ui-card'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  placeholder={t('jobs.notePlaceholder', 'e.g. Gate code is 4321. Dog in backyard.')}
                  rows={2}
                  className="rounded-lg border border-ui-border bg-white px-3 py-2 text-[13px] resize-none focus:outline-none focus:border-brand-dark dark:bg-ui-card"
                />
                {saveError && <p className="text-[11.5px] text-red-600">{saveError}</p>}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setAddNoteOpen(false)}
                    className="rounded-full px-3 py-1 text-[12px] text-ui-text2 hover:bg-ui-hover"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={!noteText.trim() || saving}
                    onClick={() => void handleSave()}
                    className="rounded-full bg-brand-dark px-3 py-1 text-[12px] font-semibold text-white disabled:opacity-40 hover:opacity-90"
                  >
                    {saving ? t('common.saving', 'Saving…') : t('jobs.saveNote', 'Save note')}
                  </button>
                </div>
              </div>
            )}

            {visibleText.length > 0 ? (
              <div className="flex flex-col gap-[9px]">
                {visibleText.map(n => <TextNoteCard key={n.note_id} note={n} />)}
              </div>
            ) : !addNoteOpen && (
              <p className="text-[12px] text-ui-text3 py-2">
                {notes.length === 0
                  ? t('jobs.noNotesYet', 'No notes yet. Use "+ Add note" to capture site details.')
                  : t('jobs.noNotesInFilter', 'No text notes in this category.')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
